import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftRight, Check, Download } from "lucide-react";
import React from "react";

interface StaffShiftTableProps {
    onStaffSelect?: (staffId: string) => void;
}

const StaffShiftTable: React.FC<StaffShiftTableProps> = ({ onStaffSelect }) => {
    const navigate = useNavigate();
    const staffData = [
        {
            id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
            name: "Sarah Miller",
            role: "Lead Pharmacist",
            shift: "Morning",
            rxVerified: 85,
            rxAccuracy: "99.8%",
            status: "Active",
            actionType: "Reassign"
        },
        {
            id: "b2c3d4e5-f6a7-8901-bcde-f12345678901",
            name: "John Davis",
            role: "Pharmacist",
            shift: "Afternoon",
            rxVerified: 65,
            rxAccuracy: "99.5%",
            status: "Off",
            actionType: "Review"
        },
        {
            id: "c3d4e5f6-a7b8-9012-cdef-123456789012",
            name: "Emily Rose",
            role: "Pharmacist",
            shift: "Morning",
            rxVerified: 95,
            rxAccuracy: "99.2%",
            status: "Active",
            actionType: "Approve"
        },
        {
            id: "d4e5f6a7-b8c9-0123-def1-234567890123",
            name: "Mike Thompson",
            role: "Tech",
            shift: "Night",
            rxVerified: 45,
            rxAccuracy: "98.5%",
            status: "Active",
            actionType: "Reject"
        },
        {
            id: "e5f6a7b8-c9d0-1234-ef12-345678901234",
            name: "Lisa Wong",
            role: "Tech",
            shift: "Afternoon",
            rxVerified: 58,
            rxAccuracy: "99.0%",
            status: "Break",
            actionType: "Complete"
        }
    ];

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "Active":
                return <Badge className="bg-green-600 hover:bg-green-600 text-white">Active</Badge>;
            case "Off":
                return <Badge className="bg-red-500 hover:bg-red-500 text-white">Off</Badge>;
            case "Break":
                return <Badge className="bg-orange-500 hover:bg-orange-500 text-white">Break</Badge>;
            default:
                return <Badge variant="secondary">{status}</Badge>;
        }
    };

    const handleRowClick = (staffId: string) => {
        // Use callback if provided, otherwise navigate to detail route
        if (onStaffSelect) {
            onStaffSelect(staffId);
        } else {
            navigate({ to: '/manager/employee-performance/$employeeId', params: { employeeId: staffId } });
        }
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row items-center justify-between w-full mb-4">
                <span className="text-xl font-medium">Current Shift Status</span>
                <div className="flex flex-row items-center justify-end gap-x-2">
                    <Button variant="outline"><Download className="h-5 w-5" /></Button>
                    <Button variant="outline"><ArrowLeftRight className="mr-2 h-4 w-4" />Sort</Button>
                </div>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="text-black">Staff Member</TableHead>
                        <TableHead className="text-black text-center">Shift</TableHead>
                        <TableHead className="text-black text-center">RX Verified</TableHead>
                        <TableHead className="text-black text-center">Rx Accuracy</TableHead>
                        <TableHead className="text-black text-center">Status</TableHead>
                        <TableHead className="text-black text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {staffData.map((staff) => (
                        <TableRow 
                            key={staff.id} 
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleRowClick(staff.id)}
                        >
                            <TableCell>
                                <div className="flex flex-col">
                                    <span className="font-medium text-gray-900">{staff.name}</span>
                                    <span className="text-sm text-gray-500">{staff.role}</span>
                                </div>
                            </TableCell>
                            <TableCell className="text-center">{staff.shift}</TableCell>
                            <TableCell className="text-center">{staff.rxVerified}</TableCell>
                            <TableCell className="text-center">{staff.rxAccuracy}</TableCell>
                            <TableCell className="text-center">{getStatusBadge(staff.status)}</TableCell>
                            <TableCell className="text-right">
                                <div className="flex flex-row items-center justify-end gap-x-2">
                                    {staff.actionType === "Reassign" && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log("Reassign clicked for", staff.id);
                                            }}
                                        >
                                            Reassign
                                        </Button>
                                    )}
                                    {staff.actionType === "Review" && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log("Review clicked for", staff.id);
                                            }}
                                        >
                                            <Check className="mr-1 h-4 w-4" />
                                            Review
                                        </Button>
                                    )}
                                    {staff.actionType === "Approve" && (
                                        <Button 
                                            size="sm"
                                            className="bg-blue-600 hover:bg-blue-700"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log("Approve clicked for", staff.id);
                                            }}
                                        >
                                            <Check className="mr-1 h-4 w-4" />
                                            Approve
                                        </Button>
                                    )}
                                    {staff.actionType === "Reject" && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log("Reject clicked for", staff.id);
                                            }}
                                        >
                                            Reject
                                        </Button>
                                    )}
                                    {staff.actionType === "Complete" && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log("Complete clicked for", staff.id);
                                            }}
                                        >
                                            Complete
                                            <div className="ml-2 w-4 h-4 rounded-full border-2 border-gray-400" />
                                        </Button>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

export default StaffShiftTable;
